import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { createMcpAuthorization, sessionExpiredBus, type McpAuthorizationWorkspace } from '../api.js';
import { getMcpAuthorizationWorkspaces } from '../lib/mcp-authorize-api.js';
import { clearMcpAuthorization, rememberMcpAuthorization } from '../lib/mcp-auth-return.js';
import { classifyMcpAuthorizeError, mcpAuthorizeFailureMessage, type McpAuthorizeFailure } from '../lib/mcp-authorize-errors.js';
import { supabase } from '../lib/supabaseClient.js';
import { Login } from './Login.js';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
type ShownFailure = Exclude<McpAuthorizeFailure, { kind: 'sign_in' }>;

/** Drop this tab's portal session and show the sign-in form on this same page.
 * The URL is untouched, so an email/password sign-in retries the workspaces call in
 * place (the page re-keys on the new user id). The saved handle carries the full query
 * string, so a GitHub/Google/magic-link sign-in comes back here via AuthCallback.
 * sessionExpiredBus is the portal's existing "session is dead" path: AuthProvider clears
 * user state at once and resets the bus on the next sign-in. The local sign-out clears the
 * stale tokens so the old account cannot come back on a token refresh. */
export function returnToMcpSignIn(requestId: string): void {
  rememberMcpAuthorization(requestId, Date.now(), window.location.search);
  sessionExpiredBus.emit();
  void supabase.auth.signOut({ scope: 'local' }).catch(() => undefined);
}

/** Props only (no hooks), so it renders with renderToStaticMarkup in tests. */
export function McpAuthorizeFailureNotice({ failure, onSwitchAccount }: { failure: ShownFailure; onSwitchAccount: () => void }) {
  return <div role="alert">
    <p>{mcpAuthorizeFailureMessage(failure)}</p>
    {failure.kind === 'no_operator_membership' && <p>
      <button type="button" onClick={onSwitchAccount}>Sign out and switch account</button>
    </p>}
    <p><small>Error code: <code>{failure.code}</code></small></p>
  </div>;
}

export function McpAuthorizePage() {
  const { user } = useAuth();
  return <McpAuthorizeSession key={user?.id ?? 'signed-out'} />;
}
function McpAuthorizeSession() {
  const { user, loading } = useAuth();
  const requestId = new URLSearchParams(window.location.search).get('authorization_request_id') ?? '';
  const [workspaces, setWorkspaces] = useState<McpAuthorizationWorkspace[]>([]);
  const [clientName, setClientName] = useState('your MCP client');
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [failure, setFailure] = useState<ShownFailure | null>(null);
  const switchAccount = (): void => { returnToMcpSignIn(requestId); };
  useEffect(() => {
    if (!UUID.test(requestId)) return;
    if (!user) { rememberMcpAuthorization(requestId, Date.now(), window.location.search); return; }
    let cancelled = false;
    setReady(false); setWorkspaces([]); setError(''); setFailure(null);
    void getMcpAuthorizationWorkspaces(requestId).then((result) => {
      if (cancelled) return;
      setWorkspaces(result.workspaces); setClientName(result.client_name); setReady(true);
      // ocn-S1172-P0: an account with exactly one verified workspace has nothing
      // to choose. Go straight to the consent page instead of showing a
      // one-button picker. The MC request is single-use, so this runs once per
      // effect and the cancelled flag stops a stale effect from consuming it.
      if (result.workspaces.length === 1) void selectWorkspace(result.workspaces[0].membership_id);
    }).catch((err: unknown) => {
      if (cancelled) return;
      const classified = classifyMcpAuthorizeError(err);
      // Not signed in (no session, or the API answered 401): show the sign-in form here
      // instead of a dead end. The request is not consumed by a 401, so it is retried.
      if (classified.kind === 'sign_in') { returnToMcpSignIn(requestId); return; }
      setFailure(classified);
    });
    return () => { cancelled = true; };
  }, [requestId, user?.id]);
  async function selectWorkspace(membershipId: string): Promise<void> {
    setBusy(true); setError('');
    try {
      const result = await createMcpAuthorization(requestId, membershipId);
      if (result.agent_approval_id && !result.transaction_id && !result.operator_approval_id && UUID.test(result.agent_approval_id)) {
        clearMcpAuthorization();
        window.location.assign(`https://mission-control.lumeniq.ai/oauth/authorize/agent-return?agent_approval_id=${encodeURIComponent(result.agent_approval_id)}`);
        return;
      }
      if (result.operator_approval_id && !result.agent_approval_id && !result.transaction_id && UUID.test(result.operator_approval_id)) {
        clearMcpAuthorization();
        window.location.assign(`https://mission-control.lumeniq.ai/oauth/authorize/operator-return?operator_approval_id=${encodeURIComponent(result.operator_approval_id)}`);
        return;
      }
      if (!result.transaction_id || result.operator_approval_id || result.agent_approval_id || !UUID.test(result.transaction_id)) throw new Error('Invalid authorization response');
      clearMcpAuthorization();
      window.location.assign(`https://mission-control.lumeniq.ai/oauth/authorize/subscriber-return?mcp_transaction_id=${encodeURIComponent(result.transaction_id)}`);
    } catch (error: unknown) {
      const reason = error instanceof Error ? error.message : '';
      if (reason === 'membership_inactive') {
        setFailure({ kind: 'no_operator_membership', code: reason });
        setBusy(false);
        return;
      }
      setError(reason === 'live_transaction_exists'
        ? 'Another connection for this client is being set up right now. Wait a few seconds, then restart the connection from your MCP client.'
        : reason === 'already_redeemed'
          ? 'This connection link has already been used. Restart the connection from your MCP client.'
          : 'Unable to authorize this workspace. Restart the connection from your MCP client.');
      setBusy(false);
    }
  }
  if (!UUID.test(requestId)) return <main><h1>OCNexus MCP</h1><p>This connection link is invalid. Restart from your MCP client.</p></main>;
  if (loading) return <main><p>Checking your account...</p></main>;
  if (!user) return <Login onAuthenticated={() => undefined} />;
  const singleWorkspace = ready && workspaces.length === 1;
  return <main style={{ maxWidth: 560, margin: '64px auto', padding: 24 }}>
    <h1>Connect OCNexus</h1>
    {!singleWorkspace && !failure && <p>Choose a workspace for {clientName}. You will review access on the next page.</p>}
    {failure && <McpAuthorizeFailureNotice failure={failure} onSwitchAccount={switchAccount} />}
    {error && <p role="alert">{error}</p>}
    {!ready && !error && !failure && <p>Verifying your workspaces...</p>}
    {singleWorkspace && !error && !failure && <p>Connecting {workspaces[0].name} to {clientName}...</p>}
    {ready && workspaces.length === 0 && <div role="status">
      <p>{clientName} can't connect to an OCNexus workspace with this account. You're signed in as <strong>{user.email}</strong>.</p>
      <ul>
        <li>Connecting your own OCNexus workspace? Add the connector in {clientName} with the URL <code>https://mcp.ocnexus.dev/subscriber</code>, then sign in again.</li>
        <li>Connecting as an OCNexus operator? Switch to your operator account below; you will come back to this page.</li>
      </ul>
      <p><button type="button" onClick={switchAccount}>Sign out and switch account</button></p>
    </div>}
    {!singleWorkspace && !failure && workspaces.map((workspace: McpAuthorizationWorkspace) => <p key={workspace.membership_id}>
      <button disabled={busy} onClick={() => { void selectWorkspace(workspace.membership_id); }}>{workspace.name}</button>
    </p>)}
  </main>;
}
